import type { EvidenceItem } from "./types";

/**
 * Package #2.1 — real evidence behind stage and 8D gates.
 *
 * An evidence item only counts toward a gate when it carries a file (stored in
 * the private `record-evidence` bucket) or a reference (URL or document /
 * system reference ID with a description). Seeded demo evidence stays visible
 * and counts, clearly labelled. Items click-attached by name only in earlier
 * sessions stay visible but no longer satisfy a gate.
 */

export type EvidenceAttachment =
  | { kind: "file"; fileName: string; filePath: string }
  | { kind: "reference"; reference: string; description: string };

export interface EvidenceActor {
  name: string;
  title: string;
}

export const LEGACY_SUMMARY_PREFIX = "Attached in session.";

export type EvidenceProvenance = "file" | "reference" | "seeded" | "legacy";

export function evidenceProvenance(
  item: Pick<EvidenceItem, "source" | "summary">,
): EvidenceProvenance {
  if (item.source === "file") return "file";
  if (item.source === "reference") return "reference";
  if (item.summary?.startsWith(LEGACY_SUMMARY_PREFIX)) return "legacy";
  return "seeded";
}

export const PROVENANCE_LABEL: Record<EvidenceProvenance, string> = {
  file: "File",
  reference: "Reference",
  seeded: "Initial evidence",
  legacy: "Name only — no file or reference (does not satisfy the gate)",
};

/** True when the item is real evidence for gate purposes. */
export function countsTowardGate(item: Pick<EvidenceItem, "source" | "summary">): boolean {
  return evidenceProvenance(item) !== "legacy";
}

/** Returns an error message, or null when the attachment is acceptable. */
export function validateAttachment(a: EvidenceAttachment | null | undefined): string | null {
  if (!a) return "Attach a file or enter a reference.";
  if (a.kind === "file") {
    if (!a.fileName?.trim() || !a.filePath?.trim()) return "Choose a file to upload.";
    return null;
  }
  if (!a.reference?.trim()) return "Enter a URL or a document / system reference ID.";
  if (!a.description?.trim()) return "Add a short description of the reference.";
  if (a.description.trim().length < 5) return "The description is too short.";
  return null;
}

export function validateActor(actor: EvidenceActor | null | undefined): string | null {
  if (!actor || !actor.name.trim()) return "You must be signed in to attach evidence.";
  return null;
}

export function buildEvidenceItem(
  name: string,
  attachment: EvidenceAttachment,
  actor: EvidenceActor,
  at: string,
  id: string,
): EvidenceItem {
  const by = actor.title ? `${actor.name}, ${actor.title}` : actor.name;
  if (attachment.kind === "file") {
    return {
      id,
      name,
      type: "aggregate-analytics",
      addedAt: at,
      addedBy: actor.name,
      attachedByTitle: actor.title,
      source: "file",
      fileName: attachment.fileName,
      filePath: attachment.filePath,
      summary: `File "${attachment.fileName}" uploaded by ${by}.`,
    };
  }
  return {
    id,
    name,
    type: "aggregate-analytics",
    addedAt: at,
    addedBy: actor.name,
    attachedByTitle: actor.title,
    source: "reference",
    reference: attachment.reference.trim(),
    description: attachment.description.trim(),
    summary: `Reference ${attachment.reference.trim()} — ${attachment.description.trim()} (attached by ${by}).`,
  };
}

/** Storage path for an uploaded evidence file. */
export function evidenceFilePath(
  recordId: string,
  gateId: string,
  fileName: string,
  stamp: string,
) {
  const safe = fileName.replace(/[^A-Za-z0-9._-]+/g, "_").slice(-120);
  return `${recordId}/${gateId}/${stamp}-${safe}`;
}
